import { useQuery } from "@apollo/client";
import * as d3 from "d3";
import { Box, Spinner } from "grommet";
import { useState } from "react";
import {
  GET_DENY_WAR_CONTRIBUTOR,
  GET_TOP_TIPPERS,
} from "../../queries/user_queries";
import { UserCount } from "../../types/DenyStatus";
import { DateRange } from "../../types/Util";

export type TreeNode = {
  type: "node";
  value: number;
  name: string;
  children: Tree[];
  color?: string;
};
export type TreeLeaf = {
  type: "leaf";
  name: string;
  value: number;
  color: string;
};

export type Tree = TreeNode | TreeLeaf;

type CircularPackingProps = {
  width: number;
  height: number;
  data: Tree;
  largest: number;
};

export const CirclePage = () => {
  const { data: warData } = useQuery(GET_DENY_WAR_CONTRIBUTOR, {
    variables: { startDate: "2025-01-04T06:20:48.425Z" },
  });

  if (!warData?.getDenyWarContributors) {
    return <Spinner />;
  }

  const { topDeniers, topUndeniers, progressDeniers, progressUndeniers } =
    warData.getDenyWarContributors;

  const deniers: Tree[] = [...topDeniers].map((d: UserCount) => {
    return {
      type: "leaf",
      name: d.username,
      value: d.count,
      color: "#ff0000",
    };
  });

  const undeniers: Tree[] = [...topUndeniers].map((d: UserCount) => {
    return {
      type: "leaf",
      name: d.username,
      value: d.count,
      color: "#00ff00",
    };
  });
  const totalUsers: Tree[] = [...deniers, ...undeniers];
  const tree: Tree = {
    type: "node",
    name: "boss",
    value: 200,
    children: totalUsers,
  };
  return (
    <Box flex justify="center" align="center">
      <CircularPacking width={750} height={750} data={tree} largest={10} />
    </Box>
  );
};

export const TipperCirclePage = () => {
  const [selectedRange, setSelectedRange] = useState(
    DateRange.MONTH.toLowerCase()
  );
  const {
    loading,
    error,
    data: userData,
  } = useQuery(GET_TOP_TIPPERS, {
    variables: { range: selectedRange },
  });

  if (!userData?.getTopTippers) {
    return <Spinner />;
  }
  const tippers: Tree[] = userData.getTopTippers.map((t: any) => {
    return {
      type: "leaf",
      name: t.username,
      value: t.tokens,
      color: "#ddccff",
    };
  });

  const tree: Tree = {
    type: "node",
    name: "boss",
    value: 200,
    children: tippers,
  };
  const largest = tippers[0].value;
  return (
    <Box
      flex
      justify="center"
      align="center"
      background={"light-1"}
      height={"full"}
    >
      <CircularPacking width={750} height={750} data={tree} largest={largest} />
    </Box>
  );
};

export const CircularPacking = ({
  width,
  height,
  data,
  largest,
}: CircularPackingProps) => {
  const hierarchy = d3
    .hierarchy(data as Tree)
    .sum((d) => d.value)
    .sort((a, b) => b.value! - a.value!);

  const packGenerator = d3.pack<Tree>().size([width, height]).padding(4);
  const root = packGenerator(hierarchy);

  return (
    <svg width={width} height={height} style={{ display: "inline-block" }}>
      {root
        .descendants()
        .slice(1)
        .map((node) => (
          <circle
            key={node.data.name + node.data.color}
            cx={node.x}
            cy={node.y}
            r={node.r}
            strokeWidth={2}
            fill={node.data.color}
          />
        ))}
      {root
        .descendants()
        .slice(1)
        .map((node) => (
          <text
            key={node.data.name + node.data.color}
            x={node.x}
            y={node.y}
            fontSize={Math.sqrt(node.data.value / largest) * 35}
            fontWeight={0.4}
            textAnchor="middle"
            alignmentBaseline="middle"
            fill="#222222"
          >
            <tspan x={node.x} dy="0">
              {node.data.name}
            </tspan>
            <tspan x={node.x} dy="1.2em">
              {node.value?.toLocaleString()}
            </tspan>
          </text>
        ))}
    </svg>
  );
};
